import { LayoutDashboard, ListPlus, LineChart, Wallet, Target, Repeat, Banknote, FileText, ArrowLeftRight, TrendingUp, Trophy, Tag, BookOpen, Building2, Filter, Layers, Users, Receipt, Briefcase, Package, Boxes, Users2, Bot, Search, GitCompareArrows, Sliders, ScrollText } from "lucide-react";

// ---------- Nav groups (QuickBooks-inspired ordering) ----------
// Each item may declare `perm` — the permission key required to see it.
// Items without a `perm` are always shown.
// Which "edition" each item belongs to. Accountrix ships as two editions
// on the same codebase:
//   - "personal" (Budgets, Goals, Investments; hides AR/AP/Payroll/Inventory)
//   - "business" (AR/AP/Payroll/Inventory/Recon; hides Budgets/Goals)
// Items tagged BOTH show in both editions (Dashboard, Transactions, Banking,
// Reports, AI, Settings, etc.). If a workspace hasn't picked an edition yet,
// we show BOTH so nothing disappears until the user chooses.
export const ED_BOTH = ["personal", "business"];

export const GROUPS = [
  {
    label: null,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true, perm: "dashboard", editions: ED_BOTH },
      { to: "/transactions", label: "Transactions", icon: ListPlus, testid: "nav-transactions", perm: "transactions", editions: ED_BOTH },
      { to: "/transfers", label: "Transfers", icon: ArrowLeftRight, testid: "nav-transfers", perm: "transactions", editions: ED_BOTH },
      { to: "/banks", label: "Banking", icon: Banknote, testid: "nav-banks", perm: "banks", editions: ED_BOTH },
      { to: "/financials?tab=reconciliation", label: "Reconciliation", icon: GitCompareArrows, testid: "nav-reconciliation", perm: "banks", editions: ED_BOTH },
    ],
  },
  {
    label: "Sales",
    items: [
      { to: "/receivables", label: "Customers & Invoices", icon: Users, testid: "nav-receivables", perm: "ar", editions: ["business"] },
      { to: "/customer-memos", label: "AR Memos", icon: ScrollText, testid: "nav-customer-memos", perm: "ar", editions: ["business"] },
      { to: "/accounting?tab=ar-ledger", label: "AR Ledger", icon: BookOpen, testid: "nav-ar-ledger", perm: "ar", editions: ["business"] },
      { to: "/accounting?tab=ar-aging", label: "AR Statement (Aging)", icon: FileText, testid: "nav-ar-aging", perm: "ar", editions: ["business"] },
    ],
  },
  {
    label: "Expenses",
    items: [
      { to: "/payables", label: "Vendors & Bills", icon: Receipt, testid: "nav-payables", perm: "ap", editions: ["business"] },
      { to: "/vendor-memos", label: "AP Memos", icon: ScrollText, testid: "nav-vendor-memos", perm: "ap", editions: ["business"] },
      { to: "/accounting?tab=ap-ledger", label: "AP Ledger", icon: BookOpen, testid: "nav-ap-ledger", perm: "ap", editions: ["business"] },
      { to: "/accounting?tab=ap-aging", label: "AP Statement (Aging)", icon: FileText, testid: "nav-ap-aging", perm: "ap", editions: ["business"] },
      { to: "/recurring", label: "Recurring", icon: Repeat, testid: "nav-recurring", perm: "recurring", editions: ED_BOTH },
      { to: "/rules", label: "Rules", icon: Filter, testid: "nav-rules", perm: "rules", editions: ED_BOTH },
    ],
  },
  {
    label: "Employees",
    items: [
      { to: "/payroll", label: "Payroll", icon: Users2, testid: "nav-payroll", perm: "payroll", editions: ["business"] },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/inventory", label: "Products & Stock", icon: Boxes, testid: "nav-inventory", perm: "inventory", editions: ["business"] },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/chart-of-accounts", label: "Chart of Accounts", icon: Layers, testid: "nav-coa", perm: "coa", editions: ED_BOTH },
      { to: "/fixed-assets", label: "Fixed Assets", icon: Building2, testid: "nav-fixed-assets", perm: "coa", editions: ["business"] },
      { to: "/accounting", label: "Reports (P&L, BS, CF)", icon: BookOpen, testid: "nav-accounting", perm: "accounting", editions: ["business"] },
      { to: "/personal/reports", label: "Reports", icon: BookOpen, testid: "nav-personal-reports", perm: "accounting", editions: ["personal"] },
      { to: "/financials", label: "Fixed Assets & Tax", icon: Package, testid: "nav-financials", perm: "financials", editions: ["business"] },
    ],
  },
  {
    label: "Analyze",
    items: [
      { to: "/analytics", label: "Analytics", icon: LineChart, testid: "nav-analytics", perm: "analytics", editions: ED_BOTH },
      { to: "/executive", label: "Executive KPIs", icon: Briefcase, testid: "nav-executive", perm: "executive", editions: ["business"] },
      { to: "/reports", label: "Statement PDFs", icon: FileText, testid: "nav-reports", perm: "reports", editions: ED_BOTH },
      { to: "/reports/designer", label: "Report Designer", icon: Sliders, testid: "nav-report-designer", perm: "reports", editions: ["business"] },
      { to: "/ai", label: "AI Insights", icon: Bot, testid: "nav-ai", perm: "ai", editions: ED_BOTH },
    ],
  },
  {
    label: "Personal",
    items: [
      { to: "/investments", label: "Investments", icon: TrendingUp, testid: "nav-investments", perm: "investments", editions: ["personal"] },
      { to: "/goals", label: "Goals", icon: Trophy, testid: "nav-goals", perm: "goals", editions: ["personal"] },
      { to: "/budgets", label: "Budgets", icon: Target, testid: "nav-budgets", perm: "budgets", editions: ["personal"] },
      { to: "/accounts", label: "Accounts", icon: Wallet, testid: "nav-accounts", perm: "accounts", editions: ED_BOTH },
      { to: "/categories", label: "Categories", icon: Tag, testid: "nav-categories", perm: "categories", editions: ED_BOTH },
    ],
  },
];

// Quick-create actions (top-bar "+ New" button)
export const QUICK_CREATE = [
  { label: "Transaction", href: "/transactions?new=1", icon: ListPlus },
  { label: "Invoice", href: "/receivables?tab=invoices&new=1", icon: Users },
  { label: "Bill", href: "/payables?tab=bills&new=1", icon: Receipt },
  { label: "Journal Entry", href: "/chart-of-accounts?tab=journal&new=1", icon: BookOpen },
  { label: "Customer", href: "/receivables?tab=customers&new=1", icon: Users },
  { label: "Vendor", href: "/payables?tab=vendors&new=1", icon: Receipt },
  { label: "Product", href: "/inventory?tab=products&new=1", icon: Boxes },
];

// Search targets — routes searchable from the top bar
export const SEARCH_TARGETS = [
  { label: "Dashboard", to: "/", keywords: "home summary overview" },
  { label: "Transactions", to: "/transactions", keywords: "expenses income tx" },
  { label: "Transfers", to: "/transfers", keywords: "transfer move funds journal xfer" },
  { label: "Banking", to: "/banks", keywords: "plaid mono bank sync" },
  { label: "Customers & Invoices", to: "/receivables", keywords: "customer invoice ar receivables sales" },
  { label: "Vendors & Bills", to: "/payables", keywords: "vendor bill ap payables" },
  { label: "Chart of Accounts", to: "/chart-of-accounts", keywords: "coa ledger journal accounts" },
  { label: "Journal Entries", to: "/chart-of-accounts?tab=journal", keywords: "je journal double entry" },
  { label: "Reports (P&L, BS, CF)", to: "/accounting", keywords: "pnl balance sheet cashflow trial gl aging" },
  { label: "Report Designer", to: "/reports/designer", keywords: "custom report builder saved template" },
  { label: "Fixed Assets & Tax", to: "/financials", keywords: "depreciation tax vat reconciliation forecast" },
  { label: "Payroll", to: "/payroll", keywords: "employee pay run salary" },
  { label: "Inventory", to: "/inventory", keywords: "products stock cogs" },
  { label: "AI Insights", to: "/ai", keywords: "chat anomaly ai insights" },
  { label: "Executive KPIs", to: "/executive", keywords: "cfo kpi dso dpo runway margin" },
  { label: "Analytics", to: "/analytics", keywords: "chart trend analytics" },
  { label: "Rules", to: "/rules", keywords: "automation categorize" },
  { label: "Budgets", to: "/budgets", keywords: "budget spending plan" },
  { label: "Goals", to: "/goals", keywords: "savings target goal" },
  { label: "Investments", to: "/investments", keywords: "stocks crypto portfolio net worth" },
  { label: "Notifications", to: "/notifications", keywords: "alerts bell" },
  { label: "Categories", to: "/categories", keywords: "tag category" },
  { label: "Entities", to: "/entities", keywords: "company business entity" },
  { label: "Settings", to: "/settings", keywords: "profile preferences" },
];

export const linkBase = "flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-colors duration-150";
export const linkActive = "bg-[var(--brand-light)] text-[var(--brand-text)] font-medium";
export const linkIdle = "text-[var(--muted-text)] hover:bg-[var(--hover)] hover:text-[var(--text)]";

// Permission gate — returns true if user can see the item.
// Owners (permissions: ["*"]) see everything.
// Mirrors backend `ALWAYS_GRANTED` = {dashboard, settings}.
export const ALWAYS_GRANTED = new Set(["dashboard", "settings"]);
// Permission + edition gate. Owners see everything within their edition.
// If the workspace hasn't chosen a type yet (workspace_type == null), we
// show everything so the "Choose your workspace type" modal isn't blocking
// discovery of what's available.
export function canSee(user, perm, itemEditions, workspaceType) {
  if (workspaceType && itemEditions && !itemEditions.includes(workspaceType)) return false;
  if (!perm) return true;
  if (ALWAYS_GRANTED.has(perm)) return true;
  const perms = user?.permissions || [];
  if (perms.includes("*")) return true;
  return perms.includes(perm);
}

